import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Link, useParams } from "react-router";
import { admin, download, errorMessage } from "../lib/api";
import { date, EMAIL_LABEL, EMAIL_STATUS, money, ORDER_STATUS, PAYMENT_METHOD, PAYMENT_STATUS, TRANSITIONS } from "../lib/format";
import type { Address, EmailRow, List, Order } from "../lib/types";
import { Alert, Badge, Button, Card, Field, Modal, PageHeader, Spinner } from "../components/ui";

const ACTION_LABEL: Record<string, string> = {
  confirmed: "Confirmă", processing: "Începe procesarea", shipped: "Marchează expediată",
  delivered: "Marchează livrată", cancelled: "Anulează", returned: "Marchează returnată",
};

const EVENT_LABEL: Record<string, string> = {
  created: "Comandă plasată", status_changed: "Status schimbat", payment_paid: "Plată încasată", payment_refunded: "Plată rambursată",
  payment_failed: "Plată eșuată", fulfillment_created: "AWB adăugat", note_updated: "Notă actualizată",
};

export default function OrderDetail() {
  const { id } = useParams();
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["order", id], queryFn: () => admin.get<Order>(`/orders/${id}`) });
  // Emailurile pleacă asincron (worker): reîmprospătăm cât timp există unul în curs.
  const emails = useQuery({
    queryKey: ["order-emails", id],
    queryFn: () => admin.get<List<EmailRow>>(`/orders/${id}/emails`),
    refetchInterval: (query) => (query.state.data?.data.some((e) => e.status === "pending") ? 3000 : false),
  });
  const [error, setError] = useState<string>();
  const [awb, setAwb] = useState<{ carrier: string; tracking_number: string; tracking_url: string } | null>(null);

  const apply = (o: Order) => {
    qc.setQueryData(["order", id], o);
    qc.invalidateQueries({ queryKey: ["orders"] });
    // un email declanșat de modificare apare după ce worker-ul procesează evenimentul
    setTimeout(() => qc.invalidateQueries({ queryKey: ["order-emails", id] }), 1500);
    setError(undefined);
  };
  const transition = useMutation({ mutationFn: (to: string) => admin.post<Order>(`/orders/${id}/transitions`, { to }), onSuccess: apply, onError: (e) => setError(errorMessage(e)) });
  const payment = useMutation({ mutationFn: (status: string) => admin.post<Order>(`/orders/${id}/payment`, { status }), onSuccess: apply, onError: (e) => setError(errorMessage(e)) });
  const fulfill = useMutation({
    mutationFn: () => admin.post<Order>(`/orders/${id}/fulfillments`, { ...awb, mark_shipped: true }),
    onSuccess: (o) => { apply(o); setAwb(null); },
    onError: (e) => setError(errorMessage(e)),
  });

  if (q.isLoading || !q.data) return <Spinner />;
  const o = q.data, c = o.currency;
  const next = TRANSITIONS[o.status] ?? [];

  return (
    <>
      <PageHeader
        title={`Comanda #${o.number}`}
        subtitle={<><Link to="/orders" className="hover:text-brand">← Comenzi</Link> · {date(o.created_at)}</>}
        actions={<div className="flex flex-wrap gap-2">
          {next.filter((s) => s !== "cancelled" && s !== "returned").map((s) => (
            <Button key={s} onClick={() => transition.mutate(s)} disabled={transition.isPending}>{ACTION_LABEL[s]}</Button>
          ))}
          {next.includes("cancelled") && (
            <Button variant="secondary" onClick={() => confirm("Anulezi comanda? Stocul va fi returnat.") && transition.mutate("cancelled")}>Anulează</Button>
          )}
          {next.includes("returned") && <Button variant="secondary" onClick={() => transition.mutate("returned")}>Retur</Button>}
          <Button variant="secondary" onClick={() => download(`/orders/${o.id}/invoice.pdf`, `factura-comanda-${o.number}.pdf`).catch((e) => setError(errorMessage(e)))}>
            Descarcă factura
          </Button>
        </div>}
      />
      {error && <div className="mb-4"><Alert>{error}</Alert></div>}
      <div className="grid gap-6 lg:grid-cols-[2fr_1fr]">
        <div className="min-w-0 space-y-6">
          <Card title="Produse" actions={<div className="flex gap-2">
            <Badge tone={ORDER_STATUS[o.status]?.tone}>{ORDER_STATUS[o.status]?.label}</Badge>
            <Badge tone={PAYMENT_STATUS[o.payment_status]?.tone}>{PAYMENT_STATUS[o.payment_status]?.label}</Badge>
          </div>}>
            <table className="w-full text-sm">
              <tbody className="divide-y divide-slate-100">
                {o.lines.map((l) => (
                  <tr key={l.id}>
                    <td className="py-2.5">
                      <div className="font-medium">{l.title}</div>
                      <div className="text-xs text-slate-400">{l.variant_title !== "Default" && `${l.variant_title} · `}{l.sku}</div>
                    </td>
                    <td className="py-2.5 text-right text-slate-500">{l.quantity} × {money(l.unit_price, c)}</td>
                    <td className="w-28 py-2.5 text-right font-semibold">{money(l.total, c)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <dl className="ml-auto mt-4 max-w-xs space-y-1 border-t border-slate-100 pt-4 text-sm">
              <Row k="Subtotal" v={money(o.subtotal, c)} />
              {o.discount_total > 0 && <Row k={`Reducere (${o.discount_code})`} v={`−${money(o.discount_total, c)}`} />}
              <Row k={`Livrare (${o.shipping_method?.name})`} v={money(o.shipping_total, c)} />
              <Row k="din care TVA" v={money(o.tax_total, c)} muted />
              <div className="flex justify-between pt-1 text-base font-bold"><dt>Total</dt><dd>{money(o.total, c)}</dd></div>
            </dl>
          </Card>

          <Card title="Livrare" actions={o.status !== "cancelled" && <Button size="sm" variant="secondary" onClick={() => setAwb({ carrier: "", tracking_number: "", tracking_url: "" })}>+ AWB</Button>}>
            {o.fulfillments.length === 0 ? <p className="text-sm text-slate-400">Niciun AWB încă.</p> : (
              <ul className="space-y-2 text-sm">
                {o.fulfillments.map((f) => (
                  <li key={f.id} className="flex justify-between">
                    <span><b>{f.carrier}</b> · {f.tracking_url ? <a href={f.tracking_url} target="_blank" rel="noreferrer" className="text-brand">{f.tracking_number}</a> : f.tracking_number}</span>
                    <span className="text-slate-400">{date(f.created_at)}</span>
                  </li>
                ))}
              </ul>
            )}
          </Card>

          <Card title="Istoric">
            <ol className="space-y-3 border-l-2 border-slate-100 pl-4 text-sm">
              {timeline(o, emails.data?.data ?? []).map((item) => item.kind === "event" ? (
                <li key={item.e.id}>
                  <div className="font-medium">
                    {EVENT_LABEL[item.e.kind] ?? item.e.kind}
                    {item.e.kind === "status_changed" && `: ${ORDER_STATUS[item.e.data.from as string]?.label} → ${ORDER_STATUS[item.e.data.to as string]?.label}`}
                  </div>
                  <div className="text-xs text-slate-400">{date(item.e.created_at)} · {item.e.actor.split(":")[0]}</div>
                </li>
              ) : (
                <li key={item.m.id}>
                  <div className="flex flex-wrap items-center gap-2 font-medium">
                    <span>✉ {EMAIL_LABEL[item.m.template] ?? item.m.template}</span>
                    <Badge tone={EMAIL_STATUS[item.m.status]?.tone}>{EMAIL_STATUS[item.m.status]?.label ?? item.m.status}</Badge>
                  </div>
                  <div className="text-xs text-slate-400">
                    {date(item.m.sent_at ?? item.m.created_at)} · către {item.m.to}
                    {item.m.status === "failed" && item.m.last_error && <span className="text-red-600"> · {item.m.last_error}</span>}
                    {item.m.status === "pending" && item.m.attempts > 1 && <span> · reîncercare {item.m.attempts}</span>}
                  </div>
                </li>
              ))}
            </ol>
          </Card>
        </div>

        <div className="space-y-6">
          <Card title="Client">
            <div className="space-y-1 text-sm">
              <div className="font-semibold">{o.shipping_address.first_name} {o.shipping_address.last_name}</div>
              <a href={`mailto:${o.email}`} className="block text-brand">{o.email}</a>
              <a href={`tel:${o.phone}`} className="block">{o.phone}</a>
            </div>
          </Card>
          <Card title="Adresă de livrare"><AddressView a={o.shipping_address} /></Card>
          <Card title="Facturare"><AddressView a={o.billing_address} /></Card>
          <Card title="Plată">
            <div className="mb-3 text-sm">{PAYMENT_METHOD[o.payment_method] ?? o.payment_method}</div>
            {o.payment_status === "pending" && o.status !== "cancelled" && (
              <Button size="sm" onClick={() => payment.mutate("paid")} disabled={payment.isPending}>Marchează încasată</Button>
            )}
            {o.payment_status === "paid" && (
              <Button size="sm" variant="secondary" onClick={() => confirm("Marchezi plata ca rambursată?") && payment.mutate("refunded")}>Rambursează</Button>
            )}
          </Card>
          <LegalVersions v={o.legal_versions} />
          {o.note && <Card title="Observații client"><p className="whitespace-pre-line text-sm">{o.note}</p></Card>}
        </div>
      </div>

      <Modal open={!!awb} onClose={() => setAwb(null)} title="Adaugă AWB">
        {awb && (
          <form className="space-y-4" onSubmit={(e) => { e.preventDefault(); fulfill.mutate(); }}>
            <Field label="Curier"><input className="input" required list="carriers" value={awb.carrier} onChange={(e) => setAwb({ ...awb, carrier: e.target.value })} /></Field>
            <datalist id="carriers">{["FAN Courier", "Sameday", "Cargus", "DPD", "GLS"].map((c) => <option key={c} value={c} />)}</datalist>
            <Field label="Număr AWB"><input className="input" required value={awb.tracking_number} onChange={(e) => setAwb({ ...awb, tracking_number: e.target.value })} /></Field>
            <Field label="Link tracking (opțional)"><input className="input" type="url" value={awb.tracking_url} onChange={(e) => setAwb({ ...awb, tracking_url: e.target.value })} /></Field>
            <p className="text-xs text-slate-500">Comanda va fi marcată ca expediată.</p>
            <div className="flex justify-end gap-2">
              <Button type="button" variant="secondary" onClick={() => setAwb(null)}>Anulează</Button>
              <Button disabled={fulfill.isPending}>Salvează</Button>
            </div>
          </form>
        )}
      </Modal>
    </>
  );
}

function Row({ k, v, muted }: { k: string; v: string; muted?: boolean }) {
  return <div className={`flex justify-between ${muted ? "text-xs text-slate-400" : ""}`}><dt className="text-slate-500">{k}</dt><dd>{v}</dd></div>;
}

function AddressView({ a }: { a: Address }) {
  return (
    <address className="space-y-0.5 text-sm not-italic">
      {a.company && <div className="font-semibold">{a.company} · CUI {a.vat_number}{a.reg_number && ` · ${a.reg_number}`}</div>}
      <div>{a.first_name} {a.last_name}</div>
      <div>{a.address1}{a.address2 && `, ${a.address2}`}</div>
      <div>{a.city}, jud. {a.county}{a.postal_code && `, ${a.postal_code}`}</div>
      <div className="text-slate-500">{a.phone}</div>
    </address>
  );
}

type TimelineItem = { kind: "event"; at: string; e: Order["events"][number] } | { kind: "email"; at: string; m: EmailRow };

// timeline: evenimentele comenzii și emailurile trimise, cele mai noi primele.
function timeline(o: Order, emails: EmailRow[]): TimelineItem[] {
  const items: TimelineItem[] = [
    ...o.events.map((e) => ({ kind: "event" as const, at: e.created_at, e })),
    ...emails.map((m) => ({ kind: "email" as const, at: m.sent_at ?? m.created_at, m })),
  ];
  return items.sort((a, b) => new Date(b.at).getTime() - new Date(a.at).getTime());
}

// Ce versiune a termenilor și a politicii de confidențialitate era în vigoare la plasarea comenzii.
function LegalVersions({ v }: { v?: Record<string, string> }) {
  if (!v || Object.keys(v).length === 0) return null;
  const label = (x?: string) => !x ? "—" : x.startsWith("model:") ? `modelul platformei (${x.slice(6)})` : `revizia ${x.slice(-8)}`;
  return (
    <Card title="Acceptate la plasare">
      <dl className="space-y-1 text-sm">
        <div><dt className="inline text-slate-500">Termeni și condiții: </dt><dd className="inline">{label(v.terms)}</dd></div>
        <div><dt className="inline text-slate-500">Politica de confidențialitate: </dt><dd className="inline">{label(v.privacy)}</dd></div>
      </dl>
      <p className="mt-2 text-xs text-slate-400">Reviziile se văd în Pagini → istoricul paginii.</p>
    </Card>
  );
}
